import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from '@crehana/i18n';
import { Crehanaicons175 } from '@crehana/icons';
import { Loader, PrimaryButton } from '@crehana/ui';
import { secondaryLight } from '@crehana/ui/dist/styles/colors';

import { PAYMENT_METHODS_TITLES } from 'Jsx/CheckoutV4/common/constants';
import { useCheckoutContext } from 'Jsx/CheckoutV4/Context';
import { PaymentMutationVariables } from 'Jsx/CheckoutV4/Graphql/Mutation/types/PaymentMutation';
import useCustomPayment, {
  InitialPaymentVariables,
  PaymentError,
} from 'Jsx/CheckoutV4/hooks/useCustomPayment';
import { CheckoutTKeys } from 'Jsx/CheckoutV4/i18n/language';
import PaymentsLoader from 'Jsx/CheckoutV4/shared/PaymentsLoader';
import SecurePayment from 'Jsx/CheckoutV4/shared/SecurePayment';
import { DegreeQuota } from 'Jsx/CheckoutV4/types';
import { CreditCardFormFields } from 'Jsx/CheckoutV4/types/FormFields';
import useSavedCards from 'Jsx/Shared/Hooks/useSavedCards';

import useCustomerExtraData from '../../../hooks/useCustomerExtraData';
import { useMercadoPago } from '../../../hooks/useMercadoPago';
import LegalCheckbox from '../../LegalCheckbox';
import NewsLetterCheckbox from '../../NewsLetterCheckbox';
import QuotasSelector from '../../QuotasSelector';
import CardHolderInput, {
  CardHolderSate,
} from '../../UserInputFields/CardHolderInput/CardHolderInput';
import CreditCardInput, {
  CreditCardState,
} from '../../UserInputFields/CreditCardInput/CreditCardInput';
import EmailInput from '../../UserInputFields/EmailInput';

import DLocal, { DLocalData } from './components/DLocal';
import PaymentWithSavedCard from './components/SavedCardPayment';
import UserDataInput from './components/UserDataInput';
import { CreditCardFormProps, MercadoPagoQuotas, UserData } from './types';
import {
  areDlocalSavedCardsCorrect,
  areUserFieldsCompletedAndValid,
  getNextPaymentMethod,
  getUnionOfPaymentMethodsInputFields,
  isConekta,
  isDLocal,
  isDLocalOnPaymentMethods,
  isEBanx,
  isPayU,
  isStripe,
  isStripeMexico,
} from './utils';
import {
  createConektaToken,
  createDLocalToken,
  createEbanxToken,
  createPayUToken,
  createStripeMexicoToken,
  createStripeToken,
  generateDeviceId,
} from './utils/checkoutHandling';

enum FormStep {
  STEP_ONE,
  STEP_TWO,
}

const CreditCardForm: React.FC<CreditCardFormProps> = ({
  selectedQuotaNumber,
  symbol,
  quotas,
  setSelectedQuotaNumber,
}) => {
  const [checkedTerms, setCheckedTerms] = useState<boolean>(true);
  const [checkedNewsLetterTerms, setCheckedNewsLetterTerms] =
    useState<boolean>(false);
  const {
    onlinePaymentMethods,
    selectedPaymentMethod,
    fallbackToCreditCardPaymentMethod,
    checkoutProductDetail,
    countryPrefix,
  } = useCheckoutContext();

  const { executePayment } = useCustomPayment();

  const { customerExtraData, loading: loadingCustomerExtraData } =
    useCustomerExtraData();
  const { t } = useTranslation<CheckoutTKeys>();

  const {
    savedCards,
    loading: loadingSavedCard,
    hasSavedCard,
  } = useSavedCards(selectedPaymentMethod?.paymentGatewayId);

  const [isFallbackGateway, setIsFallbackGateway] = useState<boolean>(false);
  const [isPurchaseFinished, setIsPurchaseFinished] = useState<boolean>(false);
  const [userData, setUserData] = useState<UserData>({});
  const [cardHolder, setCardHolder] = useState<CardHolderSate>();
  const [creditCard, setCreditCard] = useState<CreditCardState>();
  const [dLocalCreditCardState, setDLocalCreditCardState] =
    useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);
  const [payWithSavedCards, setPayWithSavedCards] = useState<boolean>(true);
  const [dLocalCheckoutData, setDLocalCheckoutData] = useState<DLocalData>();

  const [isCreditCardInputLoading, setIsCreditCardInputLoading] =
    useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<FormStep>(FormStep.STEP_ONE);
  const { loadMercadoPago } = useMercadoPago();
  const formFields = useMemo<CreditCardFormFields>(
    () =>
      getUnionOfPaymentMethodsInputFields(onlinePaymentMethods, countryPrefix),
    [onlinePaymentMethods, countryPrefix],
  );

  const [loadingMercadoPagoQuotas, setLoadingMercadoPagoQuotas] =
    useState(false);
  const [mercadoPagoQuotas, setMercadoPagoQuotas] = useState<MercadoPagoQuotas>(
    { bin: '' },
  );

  const hasTwoSteps = formFields.cityAndState && formFields.address;

  useEffect(() => {
    if (!loadingSavedCard) setPayWithSavedCards(hasSavedCard);
  }, [loadingSavedCard, hasSavedCard]);

  const isMercadoPagoMX = () =>
    selectedPaymentMethod?.title ===
    PAYMENT_METHODS_TITLES.MERCADOPAGO_CARD_MEXICO;

  useEffect(() => {
    setIsCreditCardInputLoading(true);
  }, [selectedPaymentMethod]);

  const loadInstallments = useCallback(
    async (bin: string) => {
      const amount = `${checkoutProductDetail?.price}`;
      const mercadoPago = await loadMercadoPago();
      const [installments] = await mercadoPago.getInstallments({
        amount,
        bin,
      });

      return installments;
    },
    [loadMercadoPago, checkoutProductDetail],
  );

  useEffect(() => {
    if (!isMercadoPagoMX() || loadingMercadoPagoQuotas) return;

    if (checkoutProductDetail?.type === 'course') return;

    const bin = creditCard?.number.replace(/\s+/g, '').substring(0, 6) || '';

    if (bin === mercadoPagoQuotas.bin) return;

    if (bin.length < 6) setMercadoPagoQuotas({ bin });
    else {
      setLoadingMercadoPagoQuotas(true);
      loadInstallments(bin)
        .then(installments => {
          if (!installments) setMercadoPagoQuotas({ bin });
          else {
            const { payer_costs: payerCost } = installments;

            const newQuotas: DegreeQuota[] = [];

            payerCost.forEach(
              ({
                installments: quotaNumber,
                installment_rate: interest,
                installment_amount: quotaAmount,
                total_amount: newTotalLocal,
              }) => {
                if (quotaNumber > 12) return;
                const quota = {
                  newTotalLocal,
                  usdAmountPerQuota: null,
                  quotaNumber,
                  quotaAmount,
                  interest,
                };

                newQuotas.push(quota);
              },
            );

            setMercadoPagoQuotas({ bin, quotas: newQuotas });
          }
        })
        .catch(error => {
          setErrorMessage(error.message);
        })
        .finally(() => setLoadingMercadoPagoQuotas(false));
    }
  }, [
    creditCard?.number,
    loadInstallments,
    mercadoPagoQuotas.bin,
    checkoutProductDetail?.type,
  ]);

  const setErrorOnCheckout = (message: string) => {
    setErrorMessage(message);
    setProcessing(false);
  };

  const continueToPayment = (
    extraVariables: Partial<PaymentMutationVariables>,
  ) => {
    const fixedPaymentVariables: InitialPaymentVariables = {
      paymentGateway: `${selectedPaymentMethod?.paymentGatewayId}` || '',
      itemType: checkoutProductDetail?.type,
      itemId: `${checkoutProductDetail?.originalId}` || '',
      couponCode: checkoutProductDetail?.couponCode,
      giftcard: checkoutProductDetail?.giftcard,
      membershipStartedDate: checkoutProductDetail?.membershipStartedDate,
      checkedNewsLetterTerms,
    };

    setProcessing(true);
    const variables: PaymentMutationVariables = {
      ...fixedPaymentVariables,
      ...extraVariables,
      quotaNumber: selectedQuotaNumber,
      email: userData?.email?.isValid ? userData.email.email : '',
      stripeSafeCard: checkedTerms,
      isPaymentFallback: isFallbackGateway,
    };

    executePayment(variables)
      .then(response => {
        const { data } = response;

        if (data && data.payment) {
          const { success, redirectUrl } = data.payment;

          if (success && redirectUrl) {
            setIsPurchaseFinished(true);
            window.location.href = redirectUrl;
          } else {
            setErrorOnCheckout(t('CATCH_MESSAGE'));
          }
        } else {
          throw new Error(
            'INVALID RESPONSE FROM SERVER: no data or payment fields',
          );
        }

        setProcessing(false);
      })
      .catch(error => {
        if (error instanceof PaymentError) {
          const errorMessageTranslation = t(
            error.translationKey || 'CATCH_MESSAGE',
          );

          setErrorOnCheckout(
            errorMessageTranslation === error.translationKey
              ? t('CATCH_MESSAGE')
              : errorMessageTranslation,
          );

          if (
            error.code === 'CHECKOUT_GATEWAY_ERROR' &&
            selectedPaymentMethod &&
            onlinePaymentMethods &&
            onlinePaymentMethods.length > 0
          ) {
            const nextPaymenMethod = getNextPaymentMethod(
              selectedPaymentMethod,
              onlinePaymentMethods,
            );

            fallbackToCreditCardPaymentMethod(nextPaymenMethod);
            setIsFallbackGateway(true);
          }
        } else {
          setErrorOnCheckout(t('CATCH_MESSAGE'));
        }
      });
  };

  const handleDlocalCheckout = async () => {
    const { token: dlocalToken, error } = await createDLocalToken(
      dLocalCheckoutData,
      cardHolder,
    );

    if (error) setErrorOnCheckout(error.message);
    else {
      const deviceId = generateDeviceId().toString();

      continueToPayment({
        dlocalToken,
        deviceId,
        fullName: cardHolder?.name,
        document: userData.identityDocument?.document,
        documentType: userData.identityDocument?.documentType,
      });
    }
  };

  const handleStripeCheckout = async () => {
    const { token: stripeToken, error } = await createStripeToken({
      creditCard,
    });

    if (error) setErrorOnCheckout(error.message);
    else if (isStripeMexico(selectedPaymentMethod)) {
      const { token: stripeTokenMexico, error: errorMx } =
        await createStripeMexicoToken({
          creditCard,
        });

      if (errorMx) setErrorOnCheckout(errorMx.message);
      else {
        continueToPayment({
          stripeToken,
          stripeTokenMexico,
          document: userData.identityDocument?.document,
          documentType: userData.identityDocument?.documentType,
        });
      }
    } else {
      continueToPayment({
        stripeToken,
        document: userData.identityDocument?.document,
        documentType: userData.identityDocument?.documentType,
      });
    }
  };

  const handleEbanxCheckout = async () => {
    const { token: stripeToken, error } = await createStripeToken({
      creditCard,
    });

    if (error) setErrorOnCheckout(error.message);
    else {
      const {
        token: ebanxToken,
        error: errorEbanx,
        deviceId,
        maskedCardNumber,
      } = await createEbanxToken({
        creditCard,
        cardHolder,
        countryPrefix,
      });

      if (errorEbanx) setErrorOnCheckout(errorEbanx.message);
      else {
        continueToPayment({
          stripeToken,
          ebanxToken,
          deviceId,
          // @ts-expect-error
          creditCardNumber: maskedCardNumber,
          phoneNumber: userData.phone?.phone,
          fullName: cardHolder?.name,
          city: userData.cityAndState?.city,
          state: userData.cityAndState?.state,
          zipCode: userData.zipCode?.zipCode,
          address: userData.address?.address,
          document: userData.identityDocument?.document,
          documentType: userData.identityDocument?.documentType,
        });
      }
    }
  };

  const handleConektaCheckout = async () => {
    const { token: stripeToken, error } = await createStripeToken({
      creditCard,
    });

    if (error) setErrorOnCheckout(error.message);
    else {
      const { token: conektaToken, error: errorMx } = await createConektaToken({
        creditCard,
        cardHolder,
      });

      if (errorMx) setErrorOnCheckout(errorMx.message);
      else {
        continueToPayment({
          stripeToken,
          conektaToken,
          documentType: userData.identityDocument?.documentType,
        });
      }
    }
  };

  const handlePayUCheckout = async () => {
    const {
      token: payuToken,
      cardBrand,
      error,
    } = await createPayUToken({
      creditCard,
      cardHolder,
      document: userData.identityDocument,
    });

    if (error) setErrorOnCheckout(error.message);
    else {
      continueToPayment({
        payuToken,
        payuCvv: creditCard?.cvc,
        fullName: cardHolder?.name,
        document: userData.identityDocument?.document,
        documentType: userData.identityDocument?.documentType,
        cardBrand,
        phoneNumber: userData.phone?.phone,
        deviceId: window.DEVICE_SESSION_ID || '',
      });
    }
  };

  const handleMercadoPagoMXCheckout = async () => {
    const params = {
      cardNumber: creditCard?.number.replace(/\s+/g, ''),
      cardExpirationMonth: creditCard?.expiration.split('/')[0],
      cardExpirationYear: `20${creditCard?.expiration.split('/')[1]}`,
      cardholderName: cardHolder?.name,
      securityCode: creditCard?.cvc,
    };

    const hasCreditCardValidBin = creditCard && creditCard.isValid;

    if (!hasCreditCardValidBin) {
      setErrorMessage(t('INVALID_CARD'));
      return;
    }

    const mercadoPago = await loadMercadoPago();
    const response = await mercadoPago.createCardToken(params);

    if (response?.id) {
      const installments = await loadInstallments(response.first_six_digits);

      continueToPayment({
        cardToken: response.id,
        cardBrand: installments?.payment_method_id,
        document: userData.identityDocument?.document,
        documentType: userData.identityDocument?.documentType,
      });
    } else {
      setErrorMessage('Error');
      setProcessing(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!selectedPaymentMethod) return;

    setProcessing(true);
    setErrorMessage('');

    if (isDLocal(selectedPaymentMethod)) {
      handleDlocalCheckout();
    } else if (isStripe(selectedPaymentMethod)) {
      handleStripeCheckout();
    } else if (isConekta(selectedPaymentMethod)) {
      handleConektaCheckout();
    } else if (isEBanx(selectedPaymentMethod)) {
      handleEbanxCheckout();
    } else if (isPayU(selectedPaymentMethod)) {
      handlePayUCheckout();
    } else if (isMercadoPagoMX()) {
      handleMercadoPagoMXCheckout();
    } else {
      setErrorMessage('INVALID PAYMENT METHOD. TO DEVELOP');
      setProcessing(false);
    }
  };

  const areAllFieldsCompleted = () => {
    const isEmailCompleted = formFields.email
      ? userData.email?.isValid || false
      : true;

    const isCardHolderCompleted = formFields.cardHolder
      ? cardHolder?.isValid || false
      : true;

    return (
      checkedTerms &&
      areUserFieldsCompletedAndValid({ userData, formFields }) &&
      isEmailCompleted &&
      isCardHolderCompleted &&
      (isDLocal(selectedPaymentMethod)
        ? dLocalCreditCardState
        : creditCard?.isValid)
    );
  };

  const togglePaymentWithSavedCard = () =>
    setPayWithSavedCards(!payWithSavedCards);

  if (loadingSavedCard || loadingCustomerExtraData) {
    return (
      <div className="flex justify-center w-full">
        <Loader size={36} />
      </div>
    );
  }

  const savedCardsFiltered = savedCards.filter(
    card => card?.paymentGateway === selectedPaymentMethod?.paymentGatewayId,
  );

  const areDlocalSavedCardsValid = areDlocalSavedCardsCorrect({
    selectedPaymentMethod,
    hasSavedCard,
    customerExtraData,
  });

  if (
    areDlocalSavedCardsValid &&
    payWithSavedCards &&
    savedCardsFiltered.length > 0
  )
    return (
      <PaymentWithSavedCard
        savedCards={savedCardsFiltered}
        isDLocal={isDLocal(selectedPaymentMethod)}
        symbol={symbol}
        quotas={quotas}
        selectedQuotaNumber={selectedQuotaNumber}
        setSelectedQuotaNumber={setSelectedQuotaNumber}
        togglePaymentWithSavedCard={togglePaymentWithSavedCard}
        errorMessage={errorMessage}
        onCheckout={continueToPayment}
        processing={processing}
      />
    );

  return (
    <form
      onSubmit={handleSubmit}
      className="pb-40"
      method="post"
      id="payment-form"
      data-testid="new-credit-card"
    >
      <div
        className={`${
          isCreditCardInputLoading ? 'flex' : 'hidden'
        } justify-center w-full`}
      >
        <Loader size={36} />
      </div>
      <div className={isCreditCardInputLoading ? 'hidden' : 'block'}>
        {areDlocalSavedCardsValid && savedCardsFiltered.length > 0 && (
          <button
            type="button"
            onClick={togglePaymentWithSavedCard}
            className="mb-16 text-secondary-main font-link"
          >
            <Crehanaicons175
              size={12}
              ignoreColor
              className="mr-8 fill-current text-secondary-main"
            />
            {t('GO_SAVED_CARD')}
          </button>
        )}
        <div
          className={
            !hasTwoSteps || currentStep === FormStep.STEP_ONE
              ? 'block'
              : 'hidden'
          }
        >
          <UserDataInput
            displayFields={{ ...formFields, email: false }}
            onAddressChange={address => setUserData({ ...userData, address })}
            onDocumentChange={identityDocument =>
              setUserData({ ...userData, identityDocument })
            }
            onZipCodeChange={zipCode => setUserData({ ...userData, zipCode })}
            onPhoneChange={phone => setUserData({ ...userData, phone })}
            onCityAndStateChange={cityAndState =>
              setUserData({ ...userData, cityAndState })
            }
          />

          {hasTwoSteps && (
            <div className="w-full sm:max-w-280">
              <PrimaryButton
                isAccent
                full
                data-testid="continue-button"
                disabled={
                  !areUserFieldsCompletedAndValid({ userData, formFields })
                }
                label={t('NEXT_FORM')}
                onClick={() => setCurrentStep(FormStep.STEP_TWO)}
              />
            </div>
          )}
        </div>

        <div
          className={
            !hasTwoSteps || currentStep === FormStep.STEP_TWO
              ? 'block'
              : 'hidden'
          }
        >
          {hasTwoSteps && (
            <button
              type="button"
              data-testid="previous-button"
              className="flex mb-16 font-link text-secondary-light"
              onClick={() => setCurrentStep(FormStep.STEP_ONE)}
            >
              <span className="mr-8">
                <Crehanaicons175 size={16} color={secondaryLight} />
              </span>
              <span className="normal-case">{t('PREVIOUS_FORM')}</span>
            </button>
          )}

          {isDLocal(selectedPaymentMethod) && (
            <>
              {formFields.cardHolder && (
                <CardHolderInput
                  onCardHolderChange={setCardHolder}
                  cardHolder={cardHolder?.name || ''}
                />
              )}
              <DLocal
                onCreditCardChange={setDLocalCreditCardState}
                onFieldsMounted={setDLocalCheckoutData}
                onIFramesLoaded={() => setIsCreditCardInputLoading(false)}
              />
            </>
          )}

          {(isStripe(selectedPaymentMethod) ||
            isEBanx(selectedPaymentMethod) ||
            isConekta(selectedPaymentMethod) ||
            isPayU(selectedPaymentMethod) ||
            isMercadoPagoMX()) && (
            <>
              <CreditCardInput
                onCreditCardChange={setCreditCard}
                showAnimatedCard={
                  !isDLocalOnPaymentMethods(onlinePaymentMethods)
                }
                onCardHolderChange={setCardHolder}
                showCardHolder={formFields.cardHolder}
                cardHolder={cardHolder?.name || ''}
                onLoaded={() => setIsCreditCardInputLoading(false)}
              />

              {/* 
            This div is used by PayU to create the credit card token. 
            DO NOT DELETE !!
          */}
              <div id="mylistID" />
            </>
          )}

          <EmailInput
            onEmailChange={email => setUserData({ ...userData, email })}
          />

          <QuotasSelector
            symbol={symbol}
            quotas={isMercadoPagoMX() ? mercadoPagoQuotas.quotas : quotas}
            selectedQuotaNumber={selectedQuotaNumber}
            setSelectedQuotaNumber={setSelectedQuotaNumber}
          />

          <LegalCheckbox
            className="mb-24"
            checkedTerms={checkedTerms}
            setCheckedTerms={setCheckedTerms}
          />

          <NewsLetterCheckbox
            className="mb-24"
            checkedNewsLetterTerms={checkedNewsLetterTerms}
            setCheckedNewsLetterTerms={setCheckedNewsLetterTerms}
          />

          {errorMessage && (
            <p className="mb-24 font-caption text-red-main">{errorMessage}</p>
          )}

          <div className="w-full sm:max-w-360">
            <PrimaryButton
              type="submit"
              isAccent
              size="l"
              full
              disabled={
                !areAllFieldsCompleted() || processing || isPurchaseFinished
              }
              className="mb-24"
              label={t('BUY_NOW')}
            />
          </div>
          <SecurePayment />
        </div>
      </div>
      {processing && <PaymentsLoader type="purchaseOnline" />}
    </form>
  );
};

export default CreditCardForm;
